// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import moment from 'moment-business-days';
import 'moment-timezone';

// Get the deliveryEstimate date based on time of placing the order and deliveryEstimateHours

// Configure holidays for 2026. Not sure how to make it dynamic so that it works for any year?
const newYearsDay = '01-01-2026';
const mlkDay = '01-19-2026';
const memorialDay = '05-25-2026';
const juneteenth = '06-19-2026';
const independenceDay = '07-03-2026';
const laborDay = '09-07-2026';
const thanksgiving = '11-26-2026';
const dayAfterThanksgiving = '11-27-2026';
const christmasEve = '12-24-2026';
const christmas = '12-25-2026';

moment.updateLocale('us', {
  holidays: [newYearsDay, mlkDay, memorialDay, juneteenth, independenceDay, laborDay, thanksgiving, dayAfterThanksgiving, christmasEve, christmas],
  holidayFormat: 'MM-DD-YYYY',
});

// Configure working days of a week
moment.updateLocale('us', {
  workingWeekdays: [1, 2, 3, 4, 5],
});

const checkWorkingHours = () => {
  const dateNow = moment(new Date());
  // Convert user's time to est time
  // moment tz will take DST into consideration
  const estNow = moment.tz(dateNow, 'America/New_York');

  // Configure working hours in EST
  const estDate = estNow.format('YYYY-MM-DD');
  const startTime = moment.tz(`${estDate} 00:00:00`, 'America/New_York');
  const endTime = moment.tz(`${estDate} 17:00:00`, 'America/New_York');

  // Check if an order is placed between 12am and 5pm EST
  const orderDuringWorkingHours = estNow.isBetween(startTime, endTime);

  return {
    orderDuringWorkingHours,
    estNow,
  };
};

export const projByProjDollarPerAcre = 85;

export const deliveryEstimate = (days: number) => {
  const { orderDuringWorkingHours, estNow } = checkWorkingHours();
  const workingDays = Math.ceil(days);

  const deliveryEstimateDays = orderDuringWorkingHours ? workingDays : workingDays + 1;

  const deliveryEstimateEst = moment(estNow).businessAdd(deliveryEstimateDays);
  const deliveryEstimateLocal = moment(deliveryEstimateEst).format();
  return deliveryEstimateLocal;
};

// this determines the acreCost of a deliverable
export const deliverableCost = (typeFactor: number, orderAcreage: number): number => {
  if (orderAcreage === 0) {
    return 0;
  }
  // The add-ons Breaklines and Breaklines (2D + Topo) have typeFactor values greater than 1
  // and their deliverableCost is calculated by multiplying the typeFactor with the acreage
  if (typeFactor !== 1) {
    return +(typeFactor * orderAcreage).toFixed(2);
  }
  return +typeFactor;
};

// Quote-only deliverables (e.g. Pole Tag Data, Pole Class Data) can't be priced at checkout —
// they require a manual quote (flagged via the Deliverable.quoteOnly DB field). The order flow
// hides their checkout price and shows a custom-quote notice instead.
export const isQuoteOnlyDeliverable = (item: IDeliverable): boolean => !!item.quoteOnly;

// this returns the typeFactor as a number
export const deliverableTypeFactor = (typeFactor: number | number[], is3D?: boolean): number => {
  if (Array.isArray(typeFactor)) {
    return is3D ? typeFactor[1] : typeFactor[0];
  }
  return typeFactor;
};

export const deliveryBusinessDays = (days: number) => {
  const { orderDuringWorkingHours } = checkWorkingHours();
  const workingDays = Math.ceil(days);

  const deliveryEstimateDays = orderDuringWorkingHours ? workingDays : workingDays + 1;

  return deliveryEstimateDays;
};

// format number to string with thousands separator and 2 decimals
export const formatValue = (value: number) => (value || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// format credits and dollars string with proper formatting based on flags
export const formatCreditsAndDollars = (
  credits: number,
  dollars: number,
  salesNoCredits: boolean,
  salesNoDollars: boolean,
): string => {
  if (!salesNoCredits && typeof credits !== 'number') {
    return 'N/A';
  }
  const creditsStr = salesNoCredits ? '' : `${credits.toFixed(2)} credits`;
  const formattedDollars = formatValue(dollars);
  let dollarsStr = '';
  if (!salesNoDollars) {
    dollarsStr = salesNoCredits ? `$${formattedDollars}` : ` ($${formattedDollars})`;
  }
  return `${creditsStr}${dollarsStr}`;
};

export default deliveryEstimate;
